import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./App.css";
import ToDoList from "./components/ToDoList";
import TaskDetails from "./components/TaskDetails";
import AddEditTaskModal from "./components/AddEditTaskModal";
import ManageTagsModal from "./components/ManageTagsModal";
import CalendarComponent from "./components/Calendar";
import BackupRestoreModal from "./components/BackupRestoreModal";
import LoginScreen from "./components/LoginScreen";
import { Container, Row, Col, Button, ButtonGroup, Badge } from "react-bootstrap";

const LOCAL_AUTH_KEY = "localAuth";
const DEFAULT_AUTH_STATE = {
  isLoggedIn: false,
  userLabel: "ローカルユーザー",
  loggedAt: null,
};

const getInitialAuthState = () => {
  if (typeof window === "undefined") {
    return { ...DEFAULT_AUTH_STATE };
  }
  try {
    const storedAuth = localStorage.getItem(LOCAL_AUTH_KEY);
    if (storedAuth) {
      const parsed = JSON.parse(storedAuth);
      if (typeof parsed.isLoggedIn === "boolean") {
        return { ...DEFAULT_AUTH_STATE, ...parsed };
      }
    }
  } catch (error) {
    console.error("ログイン状態の読み込みに失敗しました:", error);
    localStorage.removeItem(LOCAL_AUTH_KEY);
  }
  return { ...DEFAULT_AUTH_STATE };
};

function App() {
  const [authState, setAuthState] = useState(getInitialAuthState);
  const [tasks, setTasks] = useState(() => {
    const storedTasks = localStorage.getItem("tasks");
    return storedTasks ? JSON.parse(storedTasks) : [];
  });
  const [tags, setTags] = useState(() => {
    const storedTags = localStorage.getItem("tags");
    return storedTags ? JSON.parse(storedTags) : ["仕事", "買い物", "趣味"];
  });
  const [selectedTask, setSelectedTask] = useState(null);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showManageTagsModal, setShowManageTagsModal] = useState(false);
  const [showBackupRestoreModal, setShowBackupRestoreModal] = useState(false);

  // ログイン状態の変更をローカルストレージに保存
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(LOCAL_AUTH_KEY, JSON.stringify(authState));
    } catch (error) {
      console.error("ログイン状態の保存に失敗しました:", error);
    }
  }, [authState]);

  // タスクの変更をローカルストレージに保存
  useEffect(() => {
    try {
      localStorage.setItem("tasks", JSON.stringify(tasks));
      console.log("Tasks saved to localStorage:", tasks);
    } catch (error) {
      console.error("タスクの保存に失敗しました:", error);
      alert("タスクの保存に失敗しました。再試行してください。");
    }
  }, [tasks]);

  // タグの変更をローカルストレージに保存
  useEffect(() => {
    try {
      localStorage.setItem("tags", JSON.stringify(tags));
      console.log("Tags saved to localStorage:", tags);
    } catch (error) {
      console.error("タグの保存に失敗しました:", error);
      alert("タグの保存に失敗しました。再試行してください。");
    }
  }, [tags]);

  // タスクの追加
  const addTask = (task) => {
    setTasks([...tasks, task]);
  };

  // タスクの更新
  const updateTask = (updatedTask) => {
    setTasks(
      tasks.map((task) => (task.id === updatedTask.id ? updatedTask : task))
    );
  };

  // タスクの削除
  const deleteTask = (taskId) => {
    setTasks(tasks.filter((task) => task.id !== taskId));
    if (selectedTask && selectedTask.id === taskId) {
      setSelectedTask(null);
    }
  };

  // タグの追加
  const addTag = (newTag) => {
    if (!tags.includes(newTag)) {
      setTags([...tags, newTag]);
    }
  };

  // タグの削除
  const deleteTag = (tagToDelete) => {
    if (window.confirm(`タグ「${tagToDelete}」を削除しますか？`)) {
      setTags(tags.filter((tag) => tag !== tagToDelete));
      // タグを削除したタスクからもタグを除去
      setTasks(
        tasks.map((task) => ({
          ...task,
          tags: task.tags.filter((tag) => tag !== tagToDelete),
        }))
      );
      if (selectedTask && selectedTask.tags.includes(tagToDelete)) {
        setSelectedTask({
          ...selectedTask,
          tags: selectedTask.tags.filter((tag) => tag !== tagToDelete),
        });
      }
    }
  };

  // タスクの選択
  const selectTask = (task) => {
    setSelectedTask(task);
  };

  // タスクの完了状態を切り替え
  const toggleTaskCompletion = (taskId) => {
    setTasks(
      tasks.map((task) => {
        if (task.id === taskId) {
          return { ...task, completed: !task.completed };
        }
        return task;
      })
    );
    if (selectedTask && selectedTask.id === taskId) {
      setSelectedTask({
        ...selectedTask,
        completed: !selectedTask.completed,
      });
    }
  };

  // バックアップ機能
  const exportData = () => {
    const dataStr = JSON.stringify({ tasks, tags }, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `todo_backup_${new Date().toISOString().split("T")[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    console.log("データをバックアップしました。");
  };

  // 復元機能
  const importData = (importedData, overwrite = false) => {
    if (importedData.tasks && importedData.tags) {
      if (overwrite) {
        setTasks(importedData.tasks);
        setTags(importedData.tags);
        console.log("データを上書き復元しました。", importedData);
      } else {
        setTasks([...tasks, ...importedData.tasks]);
        setTags([
          ...new Set([...tags, ...importedData.tags, "仕事", "買い物", "趣味"]),
        ]);
        console.log("データを追加復元しました。", importedData);
      }
      alert("データの復元に成功しました。");
    } else {
      alert("不正なデータ形式です。");
    }
  };

  const handleLocalLogin = () => {
    setAuthState({
      ...DEFAULT_AUTH_STATE,
      isLoggedIn: true,
      loggedAt: new Date().toISOString(),
    });
  };

  const handleSignOut = () => {
    setAuthState({ ...DEFAULT_AUTH_STATE });
  };

  if (!authState.isLoggedIn) {
    return <LoginScreen onLogin={handleLocalLogin} />;
  }

  const incompleteCount = tasks.filter((task) => !task.completed).length;
  const upcomingCount = tasks.filter((task) => {
    if (task.completed || !task.deadline) return false;
    const deadlineDate = new Date(task.deadline);
    if (Number.isNaN(deadlineDate.getTime())) return false;
    const diff = deadlineDate - new Date();
    return diff >= 0 && diff <= 7 * 24 * 60 * 60 * 1000;
  }).length;
  const totalTagsCount = tags.length;

  return (
    <Container fluid className="app-shell h-100 d-flex flex-column px-0">
      <header className="app-nav d-flex flex-wrap align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2">
          <i className="bi bi-check2-circle text-primary fs-4"></i>
          <div>
            <div className="fw-semibold">Cohorz ToDo App</div>
            <small className="text-muted">ローカルストレージに保存中</small>
          </div>
        </div>
        <div className="d-flex align-items-center gap-3 flex-wrap justify-content-end">
          <Badge bg="success" className="auth-badge">
            <i className="bi bi-shield-check me-1"></i>
            ローカルログイン中
          </Badge>
          <span className="text-muted small">
            {authState.userLabel}
            {authState.loggedAt
              ? ` / ${new Date(authState.loggedAt).toLocaleString("ja-JP")}`
              : ""}
          </span>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleSignOut}
            aria-label="サインアウト"
          >
            <i className="bi bi-box-arrow-right me-1"></i>
            サインアウト
          </Button>
        </div>
      </header>
      <div className="main-content-wrapper flex-grow-1 w-100 px-3 pb-3">
        <Row className="h-100">
          {/* 左側のTo-Doパネル */}
          <Col xs={12} md={5} className="todo-panel">
            <div className="summary-strip shadow-sm my-3">
              <div className="summary-item">
                <div className="summary-icon bg-primary-subtle text-primary">
                  <i className="bi bi-list-check"></i>
                </div>
                <div>
                  <div className="summary-value">{incompleteCount}</div>
                  <div className="summary-label">未完了</div>
                </div>
              </div>
              <div className="summary-item">
                <div className="summary-icon bg-danger-subtle text-danger">
                  <i className="bi bi-calendar-week"></i>
                </div>
                <div>
                  <div className="summary-value">{upcomingCount}</div>
                  <div className="summary-label">今週締切</div>
                </div>
              </div>
              <div className="summary-item">
                <div className="summary-icon bg-info-subtle text-info">
                  <i className="bi bi-tags"></i>
                </div>
                <div>
                  <div className="summary-value">{totalTagsCount}</div>
                  <div className="summary-label">タグ</div>
                </div>
              </div>
            </div>
            <ToDoList
              tasks={tasks}
              tags={tags}
              selectTask={selectTask}
              toggleTaskCompletion={toggleTaskCompletion}
              selectedTask={selectedTask}
            />
            <div className="d-flex justify-content-between mt-3">
              <ButtonGroup>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setIsEditing(false);
                    setShowAddEditModal(true);
                  }}
                >
                  <i className="bi bi-plus-circle"></i> 新規作成
                </Button>
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => {
                    if (selectedTask) {
                      setIsEditing(true);
                      setShowAddEditModal(true);
                    } else {
                      alert("編集するタスクを選択してください。");
                    }
                  }}
                  disabled={!selectedTask}
                >
                  <i className="bi bi-pencil-square"></i> 編集
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    if (selectedTask) {
                      if (
                        window.confirm(
                          `タスク「${selectedTask.title}」を削除しますか？`
                        )
                      ) {
                        deleteTask(selectedTask.id);
                      }
                    } else {
                      alert("削除するタスクを選択してください。");
                    }
                  }}
                  disabled={!selectedTask}
                >
                  <i className="bi bi-trash"></i> 削除
                </Button>
              </ButtonGroup>
              <div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="me-1"
                  onClick={() => setShowManageTagsModal(true)}
                >
                  <i className="bi bi-tags"></i> タグ管理
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setShowBackupRestoreModal(true)}
                >
                  <i className="bi bi-download"></i> バックアップ/復元
                </Button>
              </div>
            </div>
          </Col>
          {/* 右側の本文表示 */}
          <Col xs={12} md={7} className="content-panel">
            <TaskDetails task={selectedTask} />
            <CalendarComponent tasks={tasks} selectTask={selectTask} />
          </Col>
        </Row>
      </div>

      {/* モーダルコンポーネント */}
      <AddEditTaskModal
        show={showAddEditModal}
        handleClose={() => setShowAddEditModal(false)}
        addTask={addTask}
        updateTask={updateTask}
        isEditing={isEditing}
        task={isEditing ? selectedTask : null}
        tags={tags}
      />

      <ManageTagsModal
        show={showManageTagsModal}
        handleClose={() => setShowManageTagsModal(false)}
        tags={tags}
        addTag={addTag}
        deleteTag={deleteTag}
      />

      <BackupRestoreModal
        show={showBackupRestoreModal}
        handleClose={() => setShowBackupRestoreModal(false)}
        exportData={exportData}
        importData={importData}
      />
    </Container>
  );
}

export default App;
